import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Breadcrumbs, CircularProgress, Container, Link, Typography } from '@mui/material';
import { routes } from '../../config/router';
import { figureCurrentPageTitle } from '../../layouts/components/MetaTitle';
import { UserContext } from '../../provider/user';
import { BreadcrumbsContext, CustomBreadcrumbsItemType } from '../../reducer/breadcrumbs';

export default function AppBreadcrumbs() {
	const { pathname } = useLocation();

	const { authorized, user } = React.useContext(UserContext);

	const {
		state: { loading, currentPageTitle, customBreadcrumbs },
	} = React.useContext(BreadcrumbsContext);

	const currentTitle = currentPageTitle
		? currentPageTitle
		: figureCurrentPageTitle({
				pathname,
				type: authorized ? 'authorized' : 'unauthorized',
				role: user !== null ? user?.role : '',
		  });

	const paths = pathname.split('/').filter((slug) => slug !== '');

	const generatePagePath = (index: number) => {
		let path = '';

		for (let i = 0; i <= index; i++) {
			path += `/${paths[i]}`;
		}

		return path;
	};

	return (
		<Box id="app-breadcrumbs" mt={5}>
			<Container>
				{loading ? (
					<CircularProgress size={14} />
				) : (
					<Breadcrumbs separator="›" aria-label="breadcrumb">
						<Link component={RouterLink} color="inherit" to="/">
							Home
						</Link>
						{customBreadcrumbs.length > 0 &&
							customBreadcrumbs.map(
								(item: CustomBreadcrumbsItemType, index: number) => {
									if (index !== customBreadcrumbs.length - 1) {
										return (
											<Link
												key={index}
												component={RouterLink}
												color="inherit"
												to={item.path}
											>
												{item.title}
											</Link>
										);
									} else {
										return (
											<Typography
												key={index}
												color="textPrimary"
												aria-current="page"
											>
												{item.title}
											</Typography>
										);
									}
								},
							)}

						{customBreadcrumbs.length === 0 &&
							paths.map((slug, index) => {
								if (index === paths.length - 1) {
									return null;
								}

								let matches = routes
									.filter(({ path }: any) => path === `/${slug}`)
									.filter(({ redirect }: any) => !redirect);

								if (!matches.length) {
									const currentPath = generatePagePath(index);

									const maybeDynamicPath = currentPath.replace(slug, ':slug');

									matches = routes
										.filter(({ path }: any) => path === maybeDynamicPath)
										.filter(({ redirect }: any) => !redirect);
								}

								if (matches.length > 1) {
									matches = matches.filter(
										({ role }: any) => role === user?.role,
									);
								}

								if (matches.length) {
									return (
										<Link
											key={slug}
											component={RouterLink}
											color="inherit"
											to={matches[0].path}
										>
											{matches[0].title}
										</Link>
									);
								}

								return null;
							})}

						{customBreadcrumbs.length === 0 && (
							<Typography color="textPrimary" aria-current="page">
								{currentTitle}
							</Typography>
						)}
					</Breadcrumbs>
				)}
			</Container>
		</Box>
	);
}
